
// Dependencies
import Vue, { PropType } from 'vue';
import { select } from 'd3-selection';
import cloud from 'd3-cloud';

// Types and constants
import { WordCloudSvgType } from './word-cloud-svg';
import { WordCloud } from '../interfaces';
import { MAX_DRAW_TIME, REDRAW_INTERVAL } from '../constants';

// Mixins
import GraphicsMixin from './graphics.mixin';
import { debounce, isEqual } from 'lodash';

export default (Vue as WordCloudSvgType).extend({
    name: 'WordCloudSvg',

    mixins: [
        GraphicsMixin,
    ],

    props: {
        width: {
            type: Number,
            required: true,
        },
        height: {
            type: Number,
            required: true,
        },
        words: {
            type: Array as PropType<WordCloud.Word[]>,
            required: true,
        },
        config: {
            type: Object as PropType<WordCloud.Config>,
            required: true,
        },
        rpx: {
            type: Number,
            required: true,
        }
    },

    data() {
        return {
            DRAW_CIRCLES: false,
            items: [] as WordCloud.Item[],
            forceSimulation: null as d3.Simulation<WordCloud.Item, undefined> | null,
            innerCenter: {
                x: 0,
                y: 0,
            },
            layout: cloud(),
            tickCounter: 0,
            debouncedHandler: Function.prototype as (() => void),
            redraw: Function.prototype as (() => void),
        };
    },

    watch: {
        words: {
            immediate: true,
            handler(newVal, oldVal) {
                if (isEqual(newVal, oldVal)) {
                    return;
                }
                this.debouncedHandler();
            },
        },

        config: {
            deep: true,
            handler(newVal, oldVal) {
                if (isEqual(newVal, oldVal)) {
                    return;
                }
                if (newVal.layout !== oldVal.layout) {
                    this.items = this.mapWords(this.words, this.items);
                    this.changeHandler();
                    this.fixRectangles;
                }
            },
        },

        width: {
            immediate: true,
            handler(newVal, oldVal) {
                if (isEqual(newVal, oldVal)) {
                    return;
                }
                this.externalRecenter();
                this.mapAndAnnotate();
                this.debouncedHandler();
            },
        },
    },

    created() {
        // If performance seems to be an issue, consider https://github.com/jasondavies/d3-cloud/issues/52
        this.debouncedHandler = debounce(
            () => this.changeHandler(),
            REDRAW_INTERVAL,
            {
                leading: true,
                trailing: true,
                maxWait: REDRAW_INTERVAL,
            }
        );

        this.items = this.mapWords(this.words);
        this.graphAnnotate();
    },

    async mounted() {
        await this.$nextTick();
        this.items = this.mapWords(this.words, this.items);
        await this.debouncedHandler();
    },

    methods: {

        async refit() {
            await this.$nextTick();
            this.fixRectangles();
        },

        async changeHandler(): Promise<void> {
            await this.$nextTick();
            this.mapAndAnnotate();
            await this.executeLayout();
            await this.refit();
        },

        mapAndAnnotate() {
            this.items = this.mapWords(this.words, this.items);
            this.graphAnnotate();
        },

        graphAnnotate() {
            select(this.$el)
                .selectAll('.smvp-word-cloud-group')
                .data(this.items, function(d) {
                    return ((d as WordCloud.Item | undefined)?.id) || select(this).attr('id');
                });
        },

        mapWords(words: WordCloud.Word[] = [], items: WordCloud.Item[] = []): WordCloud.Item[] {
            return words.map((word) => {

                const id = word?.text?.length
                    ? 'w_' + word.text.split('')
                        .reduce((acc, chr) => ((acc << 7) - acc) + chr.charCodeAt(0) % Number.MAX_SAFE_INTEGER, 5381)
                        .toString(16).toLowerCase()
                    : '';

                return {
                    ...this.origin,
                    ...(items.find((item) => item.text === word.text) || {}),
                    ...word,
                    id,
                };
            })
                .filter((item) => item.id.length);
        },

        executeLayout(): Promise<void> {
            this.graphAnnotate();
            this.$forceUpdate();
            return this.$nextTick()
                .then(() => new Promise((resolve) => {
                    this.layout = cloud()
                        .size([this.innerWidth, this.height])
                        .words(this.items)
                        .text((d) => d?.text as string)
                        .padding(Number(this.layoutSet.LAYOUT_PADDING))
                        .rotate(this.layoutSet.ROTATE_ACCESSOR as number)
                        .font(this.config.font.family)
                        .fontWeight(this.config.font.weight)
                        .fontSize((d) => this.fontSizeScale((d as WordCloud.Item).votes))
                        .spiral(this.layoutSet.SPIRAL as string)
                        .timeInterval(MAX_DRAW_TIME)
                        .on('end', (_, bounds) => {
                            if (bounds) {
                                this.internalRecenter(bounds as [ WordCloud.Point, WordCloud.Point]);
                            }
                            this.refit();
                            resolve();
                        });

                    this.layout.start();
                }));
        }
    }
});
